import React, { useState, useEffect, useRef, useMemo, createContext, useContext } from 'react';
import { X, ArrowUpRight, ChevronLeft, ChevronRight, InfoIcon, Search, SlidersHorizontal, ArrowUpDown } from 'lucide-react';
import cardsData from '../utils/cardsData.json';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { MegaMenu } from './MegaMenu';
import Footer from './Footer';
import countryData from '../utils/country.json';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaHeart, FaRegHeart } from "react-icons/fa";
import ApplicationForm from './ApplicationForm';
import { PREFERRED_COUNTRIES, STUDY_LEVELS, FIELDS_OF_STUDY } from './lib/constants';
import { useForm } from './form-provider';
import { useUser } from '../utils/auth';
import axios from 'axios';

// Add these utility functions at the top of your file
const encodeParams = (params) => {
  const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (value && value !== '') {
      if (key === 'n') {
        // Sanitize name
        acc[key] = String(value).trim().substring(0, 50) || 'Guest';
      } else {
        acc[key] = value;
      }
    }
    return acc;
  }, {});

  return btoa(JSON.stringify(cleanParams))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const decodeParams = (encoded) => {
  if (!encoded) return {};
  
  try {
    // Restore base64 padding and decode
    const padded = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (padded.length % 4)) % 4);
    return JSON.parse(atob(padded + padding));
  } catch (e) {
    console.error('Failed to decode params:', e);
    return {};
  }
};

// Create a context for saved programs
const SavedProgramsContext = createContext({
  savedPrograms: {},
  setSavedStatus: () => {}
});

// Custom hook for using the saved programs context
const useSavedPrograms = () => {
  return useContext(SavedProgramsContext);
};

// Create a context for applied programs
const AppliedProgramsContext = createContext({
  appliedPrograms: {},
  setAppliedStatus: () => {}
});

// Custom hook for using the applied programs context
const useAppliedPrograms = () => {
  return useContext(AppliedProgramsContext);
};

// Export the contexts for use in other components
export { SavedProgramsContext, useSavedPrograms, AppliedProgramsContext, useAppliedPrograms };

// SaveHeart component for saving/unsaving programs
const SaveHeart = ({ programId, size = "lg" }) => {
  const { user, isAuthenticated } = useUser();
  const { savedPrograms, setSavedStatus } = useSavedPrograms();
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginTooltip, setShowLoginTooltip] = useState(false);
  const navigate = useNavigate();
  
  const isSaved = savedPrograms[programId] || false;

  // Determine if user is truly authenticated (has valid token and user object)
  const isUserAuthenticated = useMemo(() => {
    const token = localStorage.getItem('token');
    return isAuthenticated && !!user?.id && !!token;
  }, [user, isAuthenticated]);

  // Check if the program is saved when component mounts
  useEffect(() => {
    if (isUserAuthenticated && programId) {
      checkIfSaved();
    }
  }, [isUserAuthenticated, programId]);

  // Function to check if program is saved
  const checkIfSaved = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/check-saved/${user.id}/${programId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSavedStatus(programId, response.data.isProgramSaved);
      }
    } catch (error) {
      console.error('Error checking saved status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to save or unsave program
  const toggleSave = async (e) => {
    e.stopPropagation();
    
    if (!isUserAuthenticated) {
      // Redirect to login if not authenticated
      navigate('/login');
      return;
    }

    if (isLoading) return;

    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      if (isSaved) {
        // Remove the program
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/remove-program`,
          { userId: user.id, programId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSavedStatus(programId, false);
      } else {
        // Save the program
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/save-program`,
          { userId: user.id, programId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSavedStatus(programId, true);
      }
    } catch (error) {
      console.error('Error toggling save status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  return (
    <div className="relative">
      <button
        onClick={toggleSave}
        disabled={isLoading}
        onMouseEnter={() => !isUserAuthenticated && setShowLoginTooltip(true)}
        onMouseLeave={() => setShowLoginTooltip(false)}
        className={`focus:outline-none transition-colors ${isLoading ? 'opacity-50' : 'hover:opacity-80'}`}
      >
        {isUserAuthenticated && isSaved ? (
          <FaHeart className={`${sizeClasses[size]} text-red-500`} />
        ) : (
          <FaRegHeart className={`${sizeClasses[size]} ${isUserAuthenticated ? 'text-gray-500 hover:text-red-500' : 'text-gray-400'}`} />
        )}
      </button>
      
      {showLoginTooltip && (
        <div className="absolute -top-10 right-0 bg-gray-800 text-white text-xs py-1 px-2 rounded shadow-lg whitespace-nowrap z-10">
          Login to save
          <div className="absolute bottom-0 right-2 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
        </div>
      )}
    </div>
  );
};

const Modal = ({ isOpen, onClose, children, maxWidth = "max-w-5xl" }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className={`relative w-full ${maxWidth} bg-white rounded-lg shadow-xl`} onClick={e => e.stopPropagation()}>
          {children}
        </div>
      </div>
    </div>
  );
};

export const ProgramDialog = ({ isOpen, onClose, card, isAdmin }) => {
  const [activeTab, setActiveTab] = React.useState('about');
  const { appliedPrograms } = useAppliedPrograms();
  const countryInfo = countryData.find(country => country.country === card.Location);
  const [isApplicationFormOpen, setIsApplicationFormOpen] = useState(false);
  
  // Check if the program has been applied to
  const isApplied = appliedPrograms[card._id || card.cardId] || false;

  const tabs = [
    { id: 'about', label: 'About the Course' },
    { id: 'apply', label: 'eligibility Criteria' },
    { id: 'others', label: 'Country Information' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} >
      <div className="max-h-[90vh]  overflow-y-auto p-3 sm:p-6">
        {/* Header - Made more compact on mobile */}
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0 ">

              <img
                src={card.imageSrc}
                alt={card.universityName.toUpperCase()}
                className="w-full h-full object-cover"
              />

            </div>

            <div className="flex-1">
              <h2 className="text-base sm:text-xl font-bold leading-tight">{card.universityName.toUpperCase()}</h2>
              <p className="text-gray-500 text-sm">{card.Location}</p>
            </div>

            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors sm:hidden"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={onClose}
            className="hidden sm:block p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-medium text-sm sm:text-lg uppercase">{card.programName}</h3>
            {!isAdmin && !isApplied && <SaveHeart programId={card._id || card.cardId} />}
            {!isAdmin && isApplied && (
              <span className="px-3 py-1 text-xs sm:text-sm bg-green-100 text-green-600 rounded-full font-medium">
                Applied
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <span className="px-3 py-1 text-xs sm:text-sm bg-blue-100 text-blue-600 rounded-full">
              Qualification: {card.Qualification}
            </span>
            <span className="px-3 py-1 text-xs sm:text-sm bg-green-100 text-green-600 rounded-full">
              Duration: {card.Duration}
            </span>
            <span className="px-3 py-1 text-xs sm:text-sm bg-purple-100 text-purple-600 rounded-full">
              Tuition Fees: {card.Fees.replace(/USD|GBP|AED/g, (match) => {
                return match === "USD" ? "$" : match === "GBP" ? "£" : "د.إ";
              })}
            </span>
            <span className="px-3 py-1 text-xs sm:text-sm bg-yellow-100 text-yellow-600 rounded-full">
              Entry Score (IELTS): {card["Entry Score"]}
            </span>
            <span className="px-3 py-1 text-xs sm:text-sm bg-red-100 text-red-600 rounded-full flex items-center" >
              Career Suitability: {card.careerSuitability}
              {card.careerSuitability && <FaCheckCircle className="ml-1 text-gray-500" />}
            </span>
            <span className="px-3 py-1 text-xs sm:text-sm bg-gray-100 text-gray-600 rounded-full flex items-center">
              Admission Probability: {card.admissionProbability}
              {card.admissionProbability && <FaCheckCircle className="ml-1 text-gray-500" />}
            </span>
            {!isAdmin && !isApplied && (
              <>
                <button className="bg-blue-500 text-xs lg:text-base rounded-lg px-3 lg:px-6 py-1 lg:py-0  shadow  transition font-semibold text-white  sm:w-auto flex items-center gap-2 "
                  onClick={() => window.open("https://calendly.com/applicationuni", "_blank")}>
                  Book Call
                </button>
                <button
                  onClick={() => setIsApplicationFormOpen(true)}
                  className="bg-blue-500 text-xs lg:text-base rounded-lg px-3 lg:px-6 py-1 lg:py-0 shadow transition font-semibold text-white  sm:w-auto flex items-center gap-2"
                >
                  Apply Now
                </button>
              </>
            )}
          </div>
        </div>


        {/* Tabs Navigation - Made scrollable on mobile */}
        <div className="border-b mt-4 sm:mt-6">
          <nav className="-mb-px flex space-x-4 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 text-xs sm:text-base whitespace-nowrap transition-colors flex-shrink-0 ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content - Adjusted spacing and font sizes */}
        <div className="mt-4 sm:mt-6 text-sm sm:text-base h-[60vh] ">
          {activeTab === 'about' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="space-y-3 sm:space-y-4">
                <h4 className="font-medium text-sm sm:text-lg">Program Overview</h4>
                <p className="text-gray-600 text-sm sm:text-base">{card["About the course"]}</p>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-sm sm:text-lg">Key Highlights</h4>
                <ul className="list-disc pl-4 space-y-1 text-gray-600 text-sm sm:text-base">
                  <li>Industry-relevant curriculum</li>
                  <li>Expert faculty members</li>
                  <li>Practical training and internships</li>
                  <li>State-of-the-art facilities</li>
                </ul>
              </div>

              {card.skillRecommendation && (
                <div className="space-y-2">
                  <h4 className="font-medium text-sm sm:text-lg">Recommended Skills</h4>
                  <div className="rounded-lg">
                    <div className="flex items-start gap-3">
                      <div>
                        <p className="text-sm sm:text-base">{card.skillRecommendation}</p>
                        <p className="text-xs sm:text-sm text-blue-700 mt-2 mb-5">
                          ⚠️ AI-generated recommendation based on program curriculum and industry trends
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
            </div>
          )}

          {activeTab === 'apply' && (
            <div className="space-y-4 sm:space-y-6 ">
              <h4 className="font-medium text-sm sm:text-lg">Application Process</h4>
              <p className="text-gray-600 text-sm sm:text-base">{card.parsed_output.entry_requirements_text}</p>
              
            </div>
          )}

          {activeTab === 'others' && countryInfo && (
            <div className="space-y-6 sm:space-y-8  ">
              {[
                'Living Cost',
                'Weather',
                { display: 'Part-time Work Opportunities', key: 'part_time_work' }
              ].map((section, index) => {
                // Handle both string and object section formats
                const displayText = typeof section === 'object' ? section.display : section;
                const key = typeof section === 'object' ?
                  section.key :
                  section.toLowerCase().replace(/ /g, '_');

                return (
                  <div key={index}>
                    <h4 className="font-medium text-sm sm:text-lg mb-2 sm:mb-3">{displayText}</h4>
                    <p className="text-gray-600 text-sm sm:text-base">
                      {countryInfo[key]}
                    </p>
                  </div>
                );
              })}

              <div>
                <h4 className="font-medium text-sm sm:text-lg mb-2 sm:mb-3">Post-Study Visa</h4>
                <p className="text-gray-600 text-sm sm:text-base">{card.parsed_output.post_study_visa}</p>
              </div>

              <div>
                <h4 className="font-medium text-sm sm:text-lg mb-2 sm:mb-3">Career Options</h4>
                <div className="flex flex-wrap gap-2">
                  {card.parsed_output.career_options.split(',').map((career, index) => (
                    <span
                      key={index}
                      className="px-2.5 py-1 bg-blue-50 text-blue-600 rounded-full text-xs sm:text-sm font-medium mb-5"
                    >
                      {career.trim()}
                    </span>
                  ))}
                </div>
              </div>
             
            </div>
          )}

          {activeTab === 'others' && !countryInfo && (
            <div className="text-gray-600 text-sm sm:text-base">
              Country information not available.

            </div>

          )}

        </div>
        <ApplicationForm
          isOpen={isApplicationFormOpen}
          onClose={() => setIsApplicationFormOpen(false)}
          programId={card._id || card.cardId}
          programName={card.programName}
          universityName={card.universityName}
        />

      </div>

    </Modal>
  );
};

const InfoRow = ({ label, value, showIcon }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const getTooltipContent = (label) => {
    if (label === "Career Suitability") {
      return (
        <div>
          <span>Career Suitability Score (1-10): Indicates how well this program aligns with current job market demands and career opportunities. 1-3: Limited prospects, 4-6: Moderate prospects, 7-10: Strong prospects</span> <br />
          <span style={{ color: "yellow" }}>
            ⚠️  This score is AI generated.
          </span>
        </div>
      )
    }
    if (label === "Admission Probability") {
      return (

        <div>
          <span>Admission Probability Score (1-10): Based on your academic profile compared to historical admission data. 1-3: Low chance, 4-6: Moderate chance, 7-10: High chance</span> <br />
          <span style={{ color: "yellow" }}>
            ⚠️  This score is AI generated.
          </span>
        </div>

      );
    }
    return "";
  };

  const isScoreType = label === "Career Suitability" || label === "Admission Probability";

  return (
    <div className="flex justify-between text-sm">
      <div className="flex items-center gap-1.5">
        <span className="text-gray-500">{label}</span>
        {showIcon && (
          <div
            className="relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <InfoIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
            {showTooltip && (
              <div className="absolute bottom-full left-0 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-10">
                {getTooltipContent(label)}
                <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
              </div>
            )}
          </div>
        )}
      </div>
      <span className={isScoreType ? "font-semibold" : ""}>
        {isScoreType ? `${value}` : value}
      </span>
    </div>
  );
};

const UniversityCard = ({ card }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { appliedPrograms } = useAppliedPrograms();
  
  // Check if the program has been applied to
  const isApplied = appliedPrograms[card._id || card.cardId] || false;

  // Utility function to check if the text is truncated
  const isTextTruncated = (element) => {
    if (!element) return false;
    return element.scrollWidth > element.clientWidth;
  };

  const TruncatedTextWithHover = ({ text, className }) => {
    const [isTruncated, setIsTruncated] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
      if (textRef.current) {
        setIsTruncated(isTextTruncated(textRef.current));
      }
    }, [text]);

    return (
      <div className="relative group">
        <p
          ref={textRef}
          className={`truncate ${className}`}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {text}
        </p>
        {isTruncated && (
          <div className="absolute z-10 bg-white border border-gray-300 p-2 shadow-lg rounded-md text-sm text-gray-700 hidden group-hover:block">
            {text}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg transition-shadow">
        <div className="flex items-start gap-3 mb-4">
          <div className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
            <img src={card.dgimgLink || card.imgLink}  alt={card.universityName.toUpperCase()} className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="relative group w-full max-w-[190px] lg:max-w-[310px] 2xl:max-w-[400px]">
              <h3 className="text-sm lg:text-lg font-medium text-black truncate  w-full ">
                {card.universityName.toUpperCase()}
              </h3>
              <span className="absolute left-0 top-full hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 w-max max-w-xs shadow-lg">
                {card.universityName.toUpperCase()}
              </span>
            </div>


            <p className="text-sm text-gray-500">{card.Location}</p>
          </div>
        </div>
        <div className="space-y-4">
          <TruncatedTextWithHover
            text={card.Qualification}
            className="text-sm text-gray-500"
          />
          <TruncatedTextWithHover
            text={card.programName}
            className="font-medium text-base uppercase"
          />
          <div className="flex flex-wrap gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors cursor-default">
              {card.Duration}
            </span>
            {isApplied && (
              <span className="px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-50 text-green-700 hover:bg-green-100 transition-colors cursor-default">
                Applied
              </span>
            )}
          </div>
          <InfoRow label="Location" value={card.Location} />
          <InfoRow label="Tuition Fees" value={card.Fees.replace(/USD|GBP|AED/g, (match) => {
            return match === "USD" ? "$" : match === "GBP" ? "£" : "د.إ";
          })} />
          <InfoRow label="Entry Score (IELTS)" value={card["Entry Score"]} />
          <InfoRow label="Career Suitability" value={card.careerSuitability} showIcon={true} />
          <InfoRow label="Admission Probability" value={card.admissionProbability} showIcon={true} />
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsDialogOpen(true)}
              className="w-full mr-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              Check Details
              <ArrowUpRight className="h-4 w-4" />
            </button>
            {!isApplied && <SaveHeart programId={card._id || card.cardId} size="md" />}
          </div>
        </div>
      </div>

      <ProgramDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        card={card}
        isAdmin={false}
      />
    </>
  );
};


const UniversityCards = () => {
  const location = useLocation();
  const { pagination, setPagination, formData } = useForm();
  const cardsPerPage = pagination.itemsPerPage;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [sortDialogKey, setSortDialogKey] = useState(0);
  const [programCache, setProgramCache] = useState({
    currentFilterKey: null,
    pages: {}
  });
  
  // Add savedPrograms state here to be used with the context
  const [savedPrograms, setSavedPrograms] = useState({});
  
  // Add appliedPrograms state here
  const [appliedPrograms, setAppliedPrograms] = useState({});
  
  const setSavedStatus = (programId, isSaved) => {
    setSavedPrograms(prev => ({
      ...prev,
      [programId]: isSaved
    }));
  };
  
  const setAppliedStatus = (programId, isApplied) => {
    setAppliedPrograms(prev => ({
      ...prev,
      [programId]: isApplied
    }));
  };
  
  // Provide the context values
  const savedProgramsValue = { savedPrograms, setSavedStatus };
  const appliedProgramsValue = { appliedPrograms, setAppliedStatus };

  // Fetch applied programs for current user
  const { user, isAuthenticated } = useUser();
  
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      const fetchAppliedPrograms = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/users/applied-programs/${user.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.success) {
            const appliedProgramsMap = {};
            response.data.programs.forEach(program => {
              appliedProgramsMap[program.programId] = true;
            });
            setAppliedPrograms(appliedProgramsMap);
          }
        } catch (error) {
          console.error('Error fetching applied programs:', error);
        }
      };
      
      fetchAppliedPrograms();
    }
  }, [isAuthenticated, user]);
  
  // Initialize filters from URL params
  const getInitialformData = () => {
    const encoded = searchParams.get('q');
    if (!encoded) return {};

    const decoded = decodeParams(encoded);
    return {
      preferredCountry: decoded.c || '',
      entryScore: decoded.s || '',
      universityName: decoded.u || '',
      studyLevel: decoded.l || '',
      fieldOfStudy: decoded.f || '',
      searchQuery: decoded.q || '',
      sorts: decoded.t?.split(',') || [],
      page: decoded.p || 1,
      name: decoded.n || 'Guest'
    };
  };

  const [activeFilters, setActiveFilters] = useState(() => {
    const initial = getInitialformData();
    return {
      preferredCountry: initial.preferredCountry || '',
      entryScore: initial.entryScore || '',
      universityName: initial.universityName || '',
      studyLevel: initial.studyLevel || '',
      fieldOfStudy: initial.fieldOfStudy || ''
    };
  });
  const [activeSorts, setActiveSorts] = useState(() => {
    const initial = getInitialformData();
    return initial.sorts || [];
  });
  const [searchQuery, setSearchQuery] = useState(() => {
    const initial = getInitialformData();
    return initial.searchQuery || '';
  });
  const [userName, setUserName] = useState(() => {
    const initial = getInitialformData();
    const name = initial?.name?.trim() || 'Guest';
    return name === '' ? 'Guest' : name;
  });

  // Update URL
  const updateURL = (formData) => {
    const params = {
      c: formData.preferredCountry,
      s: formData.entryScore,
      u: formData.universityName,
      l: formData.studyLevel,
      f: formData.fieldOfStudy,
      q: formData.searchQuery,
      t: formData.sorts?.join(','),
      p: pagination.currentPage > 1 ? pagination.currentPage : undefined,
      n: formData.name || userName || 'Guest'
    };

    const encoded = encodeParams(params);
    navigate(`?q=${encoded}`, { replace: true });
  };

  // Safely extract and map programs from location state
  const [rawData, setRawData] = useState(location.state?.programs || []);

  // Handle both formats: direct program objects and objects with 'og' and 'ai' properties
  const programs = useMemo(() => rawData.map((item, index) => {
    // Check if the item has og/ai structure or is a direct program object
    const isNewFormat = item?.og && item?.ai;

    if (isNewFormat) {
      return {
        ...item.og,
        admissionProbability: item.ai.admission_probability,
        careerSuitability: item.ai.career_suitability_score,
        // Add null check for parsed_output and skill_prerequisites
        skillRecommendation: item.og.parsed_output?.skill_prerequisites || "No skill prerequisites available",
        uniqueId: `program-${index}`
      };
    } else {
      // Handle direct program object
      return {
        ...item,
        // Add default values for missing properties
        parsed_output: item.parsed_output || {},
        skillRecommendation: item.skillRecommendation || "No skill prerequisites available",
        uniqueId: `program-${index}`
      };
    }
  }), [rawData]);

  const totalPages = useMemo(() => pagination.totalPages, [pagination]);

  const retryAIRecommendations = async (formData, programs, retries = 3) => {
    for (let i = 0; i < retries; i++) {
      try {
        const aiResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-recommendations`, {
          
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            formData: formData,
            programs: programs.courses,
          }),
        });

        if (!aiResponse.ok) {
          throw new Error("Failed to fetch AI recommendations");
        }

        return await aiResponse.json();
      } catch (error) {
        console.error(`Attempt ${i + 1} failed:`, error);
        if (i === retries - 1) throw error;
        // Wait for 1 second before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  };

  // submit form data to backend
  const handleSubmit = async (pageNumber, updatedFormData = formData) => {
    // Clear cache when filters or sorts change
    const filterKey = JSON.stringify(updatedFormData);
    
    // Check if filters/sorts changed
    if (!programCache.currentFilterKey || programCache.currentFilterKey !== filterKey) {
      // Clear cache when filters change
      setProgramCache({
        currentFilterKey: filterKey,
        pages: {}
      });
    } else if (programCache.pages[pageNumber]) {
      // Use cached data if available for current page
      setRawData(programCache.pages[pageNumber].courses);
      setPagination(programCache.pages[pageNumber].pagination);
      setIsLoading(false);
      return;
    }

    // update url
    updateURL(updatedFormData);
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/filter-programs?page=${pageNumber}`, {
        
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...updatedFormData,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch programs");
      }

      let programs = await response.json();
      const finalAIResponse = await retryAIRecommendations(updatedFormData, programs);
      programs.courses = finalAIResponse;

      // Cache the results for this page
      setProgramCache(prevCache => ({
        currentFilterKey: filterKey,
        pages: {
          ...prevCache.pages,
          [pageNumber]: {
            courses: finalAIResponse,
            pagination: programs.pagination
          }
        }
      }));

      setRawData(programs.courses);
      setPagination(programs.pagination);
    } catch (error) {
      console.error("Error:", error);
      setError(true);
    } finally {
      setIsLoading(false);
      setInitialLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    const updatedFormData = {
      ...formData,
      ...activeFilters,
      sorts: activeSorts,
      searchQuery: searchQuery,
      name: userName
    };
    
    const params = new URLSearchParams(searchParams);
    params.set('page', pageNumber);
    setSearchParams(params);
    
    handleSubmit(pageNumber, updatedFormData);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Update handleApplyFilters to preserve URL state when sharing but reset when filtering
  const handleApplyFilters = async (filters) => {
    // If filters are being loaded from URL (initial load), preserve all values
    const isInitialLoad = Object.values(filters).some(value => value !== '');
    
    const updatedFormData = {
        ...formData,
        ...filters,
        // Preserve sorts and search only if loading from URL
        sorts: isInitialLoad ? activeSorts : [],
        searchQuery: isInitialLoad ? searchQuery : '',
        name: userName
    };
    
    setActiveFilters(filters);
    // Only reset sorts and search if not loading from URL
    if (!isInitialLoad) {
        setActiveSorts([]);
        setSearchQuery('');
        setSortDialogKey(prev => prev + 1);
    }
    // Clear cache when filters change
    setProgramCache({
      currentFilterKey: null,
      pages: {}
    });
    await handleSubmit(1, updatedFormData);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Add handler for applying sorts
  const handleApplySorts = async (sorts) => {
    setActiveSorts(sorts);
    // Clear cache when filters change
    setProgramCache({
      currentFilterKey: null,
      pages: {}
    });
    // Include both filters and sorts in the updated form data
    const updatedFormData = {
      ...formData,
      ...activeFilters,
      sorts: sorts,
      searchQuery: searchQuery,
      name: userName
    };
    await handleSubmit(pagination.currentPage, updatedFormData);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearch = (query) => {
    setSearchQuery(query)
    // Clear cache when filters change
    setProgramCache({
      currentFilterKey: null,
      pages: {}
    });
    const updatedFormData = {
      ...formData,
      ...activeFilters,
      sorts: activeSorts,
      searchQuery: query,
      name: userName
    };
    handleSubmit(1, updatedFormData);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to generate a safe key
  const generateKey = (program) => {
    if (!program) return `empty-${Math.random().toString(36).substr(2, 9)}`;

    return program.uniqueId ||
      (program.universityName && program.programName
        ? `${program.universityName}-${program.programName}-${Math.random().toString(36).substr(2, 9)}`
        : `fallback-${Math.random().toString(36).substr(2, 9)}`
      );
  };

  // Load filters from URL on component mount
  useEffect(() => {
    const urlFormData = getInitialformData();
    const pageNumber = parseInt(searchParams.get('page')) || 1;
    const locationState = location.state;

    // First try to get data from location state (direct navigation from step-three)
    if (locationState?.searchCriteria) {
      const { searchCriteria } = locationState;
      setActiveFilters({
        preferredCountry: searchCriteria.preferredCountry || '',
        entryScore: searchCriteria.entryScore || '',
        universityName: searchCriteria.universityName || '',
        studyLevel: searchCriteria.studyLevel || '',
        fieldOfStudy: searchCriteria.fieldOfStudy || ''
      });
      setUserName(searchCriteria.name || 'Guest');
      
      // Use the programs from location state
      if (locationState.programs) {
        setRawData(locationState.programs);
        // set cache 
        setProgramCache({ 
          currentFilterKey: JSON.stringify(searchCriteria),
          pages: {
            [pageNumber]: {
              courses: locationState.programs,
              pagination: pagination
            }
          }
        });
        setInitialLoading(false);
        return;
      }
    }

    // Check if we have any URL parameters
    const hasUrlParams = Object.values(urlFormData).some(value => value !== '');
    if (hasUrlParams) {
      const structuredFormData = {
        name: urlFormData.name || 'Guest',
        email: '',
        phone: '',
        currentEducation: '',
        educationCountry: '',
        preferredCountry: urlFormData.preferredCountry || '',
        studyLevel: urlFormData.studyLevel || '',
        fieldOfStudy: urlFormData.fieldOfStudy || '',
        testScores: {
          ielts_overall: '',
          ielts_reading: '',
          ielts_writing: '',
          ielts_speaking: '',
          ielts_listening: '',
          toefl: ''
        },
        dreamCareer: '',
        proficientSubjects: '',
        entryScore: urlFormData.entryScore || '',
        universityName: urlFormData.universityName || 'all',
        sorts: urlFormData.sorts || [],
        searchQuery: urlFormData.searchQuery || ''
      };
      // If loading from URL, preserve all filters, sorts, and search
      setActiveFilters({
          preferredCountry: urlFormData.preferredCountry || '',
          entryScore: urlFormData.entryScore || '',
          universityName: urlFormData.universityName || '',
          studyLevel: urlFormData.studyLevel || '',
          fieldOfStudy: urlFormData.fieldOfStudy || ''
      });
      setActiveSorts(urlFormData.sorts || []);
      setSearchQuery(urlFormData.searchQuery || '');
      setUserName(urlFormData.name || 'Guest');

      handleSubmit(pageNumber, structuredFormData);
    } else {
      const defaultFormData = {
        name: 'Guest',
        email: '',
        phone: '',
        currentEducation: '',
        educationCountry: '',
        preferredCountry: '',
        studyLevel: '',
        fieldOfStudy: '',
        testScores: {
          ielts_overall: '',
          ielts_reading: '',
          ielts_writing: '',
          ielts_speaking: '',
          ielts_listening: '',
          toefl: ''
        },
        dreamCareer: '',
        proficientSubjects: '',
        entryScore: '',
        universityName: 'all',
        sorts: [],
        searchQuery: ''
      };
    
      handleSubmit(pageNumber, defaultFormData);
    }
  }, []);

  const sortOptions = [
    { id: 'ielts-asc', label: 'IELTS Score (Low to High)' },
    { id: 'ielts-desc', label: 'IELTS Score (High to Low)' },
    { id: 'course-asc', label: 'Course Name (A-Z)' },
    { id: 'course-desc', label: 'Course Name (Z-A)' },
    { id: 'university-asc', label: 'University Name (A-Z)' },
    { id: 'university-desc', label: 'University Name (Z-A)' }
  ];

  if (initialLoading) {
    return (
      <div>
        <MegaMenu background="bg-gray-800" />
        <div className='fixed w-screen h-screen bg-white flex flex-col justify-center items-center z-50 top-0 left-0'>
          <img src={"/2.gif"} alt="logo" className='object-contain h-1/2 aspect-auto -mb-10' />
          <h1 className='md:text-xl text-lg animate-pulse'>Loading programs...</h1>
        </div>
      </div>
    );
  }

  return (
    <SavedProgramsContext.Provider value={savedProgramsValue}>
      <AppliedProgramsContext.Provider value={appliedProgramsValue}>
        <div>
          <MegaMenu background="bg-gray-800" />
          <div className="container mx-auto p-6">
            {isLoading ? (
              <div className='fixed w-screen h-screen bg-white flex flex-col justify-center items-center z-50 top-0 left-0'>
                {!error ? (
                  <>
                    <img src={"/2.gif"} alt="logo" className='object-contain h-1/2 aspect-auto -mb-10' />
                    <h1 className='md:text-xl text-lg animate-pulse'>Finding the best programs for you...</h1>
                  </>
                ) : (
                  <div className="text-center space-y-4">
                    <h1 className="text-xl text-red-600">An error occurred while processing your request</h1>
                    <button

                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                    >
                      <Link to="/eligibility">Go Back</Link>
                    </button>
                  </div>
                )}
              </div>
            ) :
              <>
                <h2 className="text-2xl md:text-2xl lg:text-5xl font-bold text-center mb-12 text-black mt-20">
                  Eligible Programs for <span className='text-blue-600'>{(userName || 'Guest').toUpperCase()}</span>
                </h2>

                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-8">
                  <SearchBar onSearch={handleSearch} searchQuery={searchQuery}/>
                  
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setIsFilterOpen(true)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all duration-200"
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                      <span className="hidden sm:inline">Filter</span>
                    </button>
                    
                    <button
                      onClick={() => setIsSortOpen(true)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-all duration-200"
                    >
                      <ArrowUpDown className="w-4 h-4" />
                      <span className="hidden sm:inline">Sort</span>
                    </button>
                  </div>
                </div>

                <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8'>
                  <div className="flex flex-wrap gap-3 text-left md:text-center">
                    {activeFilters.preferredCountry && activeFilters.preferredCountry !== 'all' && (
                      <div>
                        <label className="block text-xs mb-1 text-green-800 text-left md:text-center">
                          Preferred Country
                        </label>
                        <div className="w-fit ml-0 md:mx-auto px-4 py-1 rounded-full text-xs font-medium text-green-800 border border-green-200">
                          {activeFilters.preferredCountry}
                        </div>
                      </div>
                    )}

                    {activeFilters.entryScore && activeFilters.entryScore !== '0' && (
                      <div>
                        <label className="block text-xs mb-1 text-red-800 text-left md:text-center">
                          IELTS's Score
                        </label>
                        <div className="w-fit ml-0 md:mx-auto px-4 py-1 rounded-full text-xs font-medium text-red-800 border border-red-200">
                          {activeFilters.entryScore}
                        </div>
                      </div>
                    )}

                    {activeFilters.studyLevel && activeFilters.studyLevel !== 'all' && (
                      <div>
                        <label className="block text-xs mb-1 text-blue-800 text-left md:text-center">
                          Study Level
                        </label>
                        <div className="w-fit ml-0 md:mx-auto px-4 py-1 rounded-full text-xs font-medium text-blue-800 border border-blue-200">
                          {activeFilters.studyLevel}
                        </div>
                      </div>
                    )}

                    {activeFilters.fieldOfStudy && activeFilters.fieldOfStudy !== 'all' && (
                      <div>
                        <label className="block text-xs mb-1 text-purple-800 text-left md:text-center">
                          Field of Study
                        </label>
                        <div className="w-fit ml-0 md:mx-auto px-4 py-1 rounded-full text-xs font-medium text-purple-800 border border-purple-200">
                          {activeFilters.fieldOfStudy}
                        </div>
                      </div>
                    )}

                    {activeFilters.universityName && activeFilters.universityName !== 'all' && (
                      <div>
                        <label className="block text-xs mb-1 text-orange-800 text-left md:text-center">
                          University
                        </label>
                        <div className="relative group">
                          <div className="w-fit ml-0 md:mx-auto px-4 py-1 rounded-full text-xs font-medium text-orange-800 border border-orange-200 max-w-[150px] truncate">
                            {activeFilters.universityName.toUpperCase()}
                          </div>
                          {/* Hover tooltip */}
                          <div className="hidden text-xs group-hover:block absolute left-1/2 transform -translate-x-1/4 top-full mt-1 px-2 py-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 whitespace-nowrap">
                            {activeFilters.universityName.toUpperCase()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {activeSorts.length > 0 && (
                    <div className="w-full md:w-auto">
                      <label className="block text-xs mb-1 text-indigo-800 text-left md:text-center">
                        Active Sorts
                      </label>
                      <div className="flex flex-wrap gap-2 justify-start">
                        {activeSorts.map(sortId => {
                          const sortOption = sortOptions.find(opt => opt.id === sortId);
                          return sortOption && (
                            <div key={sortId} className="px-4 py-1 rounded-full text-center text-xs font-medium text-indigo-800 border border-indigo-200">
                              {sortOption.label}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {!isLoading && (!programs || programs.length === 0) && 
                  <div className="text-center text-gray-600 mb-12">
                    No matching programs found. Please adjust your search criteria and try again.
                  </div>
                }

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {programs.map((program) => (
                    <UniversityCard
                      key={generateKey(program)}
                      card={program}
                    />
                  ))}
                </div>
              </>
            }
            {totalPages > 1 && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>
          <Footer />
          <FilterDialog
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            onApply={handleApplyFilters}
            programs={programs}
          />
          <SortDialog
            key={sortDialogKey}
            isOpen={isSortOpen}
            onClose={() => setIsSortOpen(false)}
            onApply={handleApplySorts}
            currentSorts={activeSorts}
          />
        </div>
      </AppliedProgramsContext.Provider>
    </SavedProgramsContext.Provider>
  );
};


const FilterDialog = ({ isOpen, onClose, onApply, programs }) => {
  // Get filters from URL encoded params
  const getInitialFilters = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const encoded = searchParams.get('q');
    
    if (!encoded) return {
      preferredCountry: '',
      entryScore: '',
      universityName: '',
      studyLevel: '',
      fieldOfStudy: '',
    };

    try {
      const decoded = decodeParams(encoded);
      return {
        preferredCountry: decoded.c || '',
        entryScore: decoded.s || '',
        universityName: decoded.u || '',
        studyLevel: decoded.l || '',
        fieldOfStudy: decoded.f || '',
      };
    } catch (e) {
      console.error('Failed to decode filters from URL:', e);
      return {
        preferredCountry: '',
        entryScore: '',
        universityName: '',
        studyLevel: '',
        fieldOfStudy: '',
      };
    }
  };

  const [filters, setFilters] = useState(getInitialFilters());
  const [universities, setUniversities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Reset filters state when dialog opens
  useEffect(() => {
    if (isOpen) {
      const initialFilters = getInitialFilters();
      setFilters(initialFilters);
    }
  }, [isOpen]);

  // Handle apply with auto-selection
  const handleApply = () => {
    const finalFilters = {
      preferredCountry: filters.preferredCountry || 'all',
      entryScore: filters.entryScore || '0',
      universityName: filters.universityName || 'all',
      studyLevel: filters.studyLevel || 'all',
      fieldOfStudy: filters.fieldOfStudy || 'all',
    };
    onApply(finalFilters);
    onClose();
  };

  const fetchUniversities = async (filters) => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        location: filters.preferredCountry || 'all',
        entryScore: filters.entryScore || '0',
        studyLevel: filters.studyLevel || 'all',
        fieldOfStudy: filters.fieldOfStudy || 'all'
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/filter-universities?${queryParams}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch universities');
      }

      const data = await response.json();
      
      if (data.success) {
        setUniversities(data.data);
      }
    } catch (error) {
      console.error('Error fetching universities:', error);
      setUniversities([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Add useEffect to fetch universities when filters change
  useEffect(() => {
    fetchUniversities(filters);
  }, [filters.preferredCountry, filters.entryScore, filters.studyLevel, filters.fieldOfStudy]);


  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth='max-w-md'>
      <div className="max-h-[90vh] overflow-y-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Filter Programs</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Filter Form */}
        <div className="space-y-4">
          <div>
            <label htmlFor="preferredCountry" className="block text-sm font-medium text-gray-700">
              Preferred Country for Education
            </label>
            <select
              id="preferredCountry"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              value={filters.preferredCountry}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                preferredCountry: e.target.value,
                universityName: '' // Reset university selection when country changes
              }))}
            >
              <option value="">All Locations</option>
              {PREFERRED_COUNTRIES.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>

          {/* Entry Score Filter */}
          <div>
          <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
            Your IELTS's Score
            <div className="relative">
              <InfoIcon 
                className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" 
                onMouseEnter={(e) => {
                  const tooltip = e.currentTarget.nextElementSibling;
                  tooltip.style.display = 'block';
                }}
                onMouseLeave={(e) => {
                  const tooltip = e.currentTarget.nextElementSibling;
                  tooltip.style.display = 'none';
                }}
              />
              <div 
                className="hidden absolute -left-1 bottom-full mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-10"
              >
                This filter will display programs with IELTS requirements at or below your score.
                <div className="absolute bottom-0 left-4 transform translate-y-1/2 -translate-x-2 rotate-45 w-2 h-2 bg-gray-800"></div>
              </div>
            </div>
          </label>
            <input
              type="number"
              value={filters.entryScore}
              onChange={(e) => setFilters(prev => ({ ...prev, entryScore: e.target.value }))}
              min="0"
              step="0.5"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter minimum IELTS score or leave empty for all"
            />
          </div>

          {/* Study Level Filter */}
          <div>
            <label htmlFor="studyLevel" className="block text-sm font-medium text-gray-700">
              Level of Study
            </label>
            <select
              id="studyLevel"
              value={filters.studyLevel}
              onChange={(e) => setFilters(prev => ({ ...prev, studyLevel: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Levels</option>
              {STUDY_LEVELS.map((level) => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          {/* Field of Study Filter */}
          <div>
            <label htmlFor="fieldOfStudy" className="block text-sm font-medium text-gray-700">
              Field of Study
            </label>
            <select
              id="fieldOfStudy"
              value={filters.fieldOfStudy}
              onChange={(e) => setFilters(prev => ({ ...prev, fieldOfStudy: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Fields</option>
              {FIELDS_OF_STUDY.map((field) => (
                <option key={field} value={field}>{field}</option>
              ))}
            </select>
          </div>

          {/* Universities according to above filters */}
          <div>
            <label htmlFor="university" className="block text-sm font-medium text-gray-700">
              University
            </label>
            <select
              id="university"
              value={filters.universityName}
              onChange={(e) => setFilters(prev => ({ ...prev, universityName: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            >
              <option value="">All Universities</option>
              {universities.map((uni) => (
                <option key={uni.universityName} value={uni.universityName}>
                  {uni.universityName.toUpperCase()}
                </option>
              ))}
            </select>
            {isLoading && <p className="text-sm text-gray-500 mt-1">Loading universities...</p>}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </Modal>
  );
};


const SortDialog = ({ isOpen, onClose, onApply, currentSorts = [] }) => {
  // Parse current sorts into field and direction
  const initialSortState = {};
  currentSorts.forEach(sort => {
    const [field, direction] = sort.split('-');
    initialSortState[field] = direction;
  });

  const [sortSelections, setSortSelections] = useState(initialSortState);

  const sortFields = [
    { id: 'ielts', label: 'IELTS Score' },
    { id: 'course', label: 'Course Name' },
    { id: 'university', label: 'University Name' }
  ];

  const handleToggleField = (fieldId) => {
    setSortSelections(prev => {
      const newSelections = { ...prev };
      
      if (newSelections[fieldId]) {
        // If field already exists, remove it
        delete newSelections[fieldId];
      } else {
        // If field doesn't exist, add it with default direction
        newSelections[fieldId] = 'asc';
      }
      
      return newSelections;
    });
  };

  const handleChangeDirection = (fieldId, direction) => {
    setSortSelections(prev => ({
      ...prev,
      [fieldId]: direction
    }));
  };

  const handleApply = () => {
    // Convert selections back to the format expected by the parent component
    const formattedSorts = Object.entries(sortSelections).map(
      ([field, direction]) => `${field}-${direction}`
    );
    onApply(formattedSorts);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth='max-w-md'>
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Sort Programs</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {sortFields.map(field => (
            <div key={field.id} className="border rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id={`field-${field.id}`}
                    checked={!!sortSelections[field.id]}
                    onChange={() => handleToggleField(field.id)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                  />
                  <label htmlFor={`field-${field.id}`} className="font-medium">
                    {field.label}
                  </label>
                </div>
                
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${sortSelections[field.id] ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'}`}>
                  {sortSelections[field.id] && (
                    <div className="flex space-x-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name={`direction-${field.id}`}
                          checked={sortSelections[field.id] === 'asc'}
                          onChange={() => handleChangeDirection(field.id, 'asc')}
                          className="form-radio h-4 w-4 text-blue-600"
                        />
                        <span className="text-sm">
                          {field.id === 'ielts' ? 'Low to High' : 'A-Z'}
                        </span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name={`direction-${field.id}`}
                          checked={sortSelections[field.id] === 'desc'}
                          onChange={() => handleChangeDirection(field.id, 'desc')}
                          className="form-radio h-4 w-4 text-blue-600"
                        />
                        <span className="text-sm">
                          {field.id === 'ielts' ? 'High to Low' : 'Z-A'}
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Apply Sort
          </button>
        </div>
      </div>
    </Modal>
  );
};

const SearchBar = ({ onSearch, searchQuery }) => {
  const [searchTerm, setSearchTerm] = useState(searchQuery);

  const handleSearch = () => {
    onSearch(searchTerm);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClear = () => {
    setSearchTerm('');
  };

  return (
    <div className="relative w-full max-w-md mx-auto md:mx-0">
      <div className="relative flex items-center">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Search programs, universities, or course descriptions..."
          className="w-full px-4 py-2 pr-20 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <div className="absolute right-0 flex items-center">
          {searchTerm && (
            <button
              onClick={handleClear}
              className="p-2 text-gray-500 hover:text-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={handleSearch}
            className="p-2 text-blue-600 hover:text-blue-700"
          >
            <Search className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const generatePageNumbers = () => {
    const pageNumbers = [];

    // Determine the start and end of the range
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    // Add range of page numbers
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const pageNumbers = generatePageNumbers();

  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-white"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>

      <div className="flex items-center gap-1">
        {currentPage > 3 && (
          <button
            onClick={() => onPageChange(1)}
            className="px-3 py-1 rounded-lg hover:bg-gray-100"
          >
            1
          </button>
        )}

        {currentPage > 4 && <span className="px-2">...</span>}

        {pageNumbers.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-1 rounded-lg ${currentPage === page
              ? 'bg-blue-600 text-white'
              : 'hover:bg-gray-100'
              }`}
          >
            {page}
          </button>
        ))}

        {currentPage < totalPages - 3 && <span className="px-2">...</span>}

        {currentPage < totalPages - 2 && (
          <button
            onClick={() => onPageChange(totalPages)}
            className="px-3 py-1 rounded-lg hover:bg-gray-100"
          >
            {totalPages}
          </button>
        )}
      </div>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-white"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
};

// Original export statement
const UniversityCardDetail = () => {
  const { id } = useParams();
  const card = cardsData.find((card) => card.cardId === id);
  
  // Add savedPrograms state here to be used with the context
  const [savedPrograms, setSavedPrograms] = useState({});
  
  const setSavedStatus = (programId, isSaved) => {
    setSavedPrograms(prev => ({
      ...prev,
      [programId]: isSaved
    }));
  };
  
  // Provide the context value
  const savedProgramsValue = { savedPrograms, setSavedStatus };

  if (!card) {
    return <div>Program not found</div>;
  }

  return (
    <SavedProgramsContext.Provider value={savedProgramsValue}>
      <UniversityCard card={card} />
    </SavedProgramsContext.Provider>
  );
};

// Export for direct use in Router
export { UniversityCards as default, UniversityCardDetail };

